/* Pretty Panther — October 2026 refresh: compact header, search take-over, member drawer,
   product page picker + "View all images", free-gift guard. */
(() => {
  const body = document.body;
  const desktop = () => window.matchMedia('(min-width: 750px)').matches;

  /* 1. Compact header once the visitor scrolls past the hero top */
  const scrollY = () => Math.max(window.scrollY || 0, document.querySelector('.page-wrapper')?.scrollTop || 0);
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      // Two thresholds so the header can't flip back and forth when it changes height:
      // compact after 200px, full size again only near the top.
      const y = scrollY();
      const compact = body.classList.contains('pp-compact');
      if (!desktop()) body.classList.remove('pp-compact');
      else if (!compact && y > 200) body.classList.add('pp-compact');
      else if (compact && y < 80) body.classList.remove('pp-compact');
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  onScroll();

  /* 2. Search take-over: dim the page while the search panel is open */
  const scrim = document.createElement('div');
  scrim.className = 'pp-search-scrim';
  scrim.setAttribute('aria-hidden', 'true');
  body.appendChild(scrim);

  const placeDrop = () => {
    const header = document.querySelector('#header-component');
    if (!header) return;
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--pp-drop-top', `${bottom}px`);
  };
  const closeSearch = () => {
    // pp-header.js closes its panel on any pointerdown outside the form
    document.body.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }));
    document.activeElement?.blur?.();
    body.classList.remove('pp-search-open');
  };
  const watchForm = (form) => {
    if (form.dataset.ppTakeover) return;
    form.dataset.ppTakeover = '1';
    new MutationObserver(() => {
      const open = form.classList.contains('is-open') && desktop();
      if (open) placeDrop();
      body.classList.toggle('pp-search-open', open);
      // the header may resize when the input grows; keep the panel attached to it
      if (open) requestAnimationFrame(placeDrop);
    }).observe(form, { attributes: true, attributeFilter: ['class'] });
  };
  // Lay live results out as "Suggestions" beside big product cards (results are written by pp-header.js).
  const shapeResults = (box) => {
    if (box.querySelector('.pp-search-drop__results-grid')) return;
    const chips = box.querySelector('.pp-search-drop__chips');
    const all = box.querySelector('.pp-search-drop__all');
    const grid = document.createElement('div');
    grid.className = 'pp-search-drop__results-grid';
    const side = document.createElement('div');
    side.className = 'pp-search-drop__suggest';
    side.innerHTML = '<p class="pp-search-drop__label">Suggestions</p>';
    if (chips) side.appendChild(chips);
    else side.insertAdjacentHTML('beforeend', '<p class="pp-search-drop__empty">Try a brand, a scent or a product type.</p>');
    const main = document.createElement('div');
    [...box.children].forEach((el) => { if (el !== all) main.appendChild(el); });
    main.querySelectorAll('.pp-search-drop__product img').forEach((img) => {
      try { const u = new URL(img.src, location.origin); u.searchParams.set('width', '400'); img.src = u.toString(); } catch {}
    });
    grid.append(side, main);
    box.prepend(grid);
  };
  const watchResults = (box) => {
    if (box.dataset.ppShaped) return;
    box.dataset.ppShaped = '1';
    new MutationObserver(() => shapeResults(box)).observe(box, { childList: true });
  };
  const initSearch = () => {
    document.querySelectorAll('[data-pp-search]').forEach(watchForm);
    document.querySelectorAll('[data-pp-search-results]').forEach(watchResults);
  };
  initSearch();
  document.addEventListener('shopify:section:load', initSearch);
  scrim.addEventListener('click', closeSearch);
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-pp-search-close]')) { e.preventDefault(); closeSearch(); }
  });
  window.addEventListener('resize', () => body.classList.contains('pp-search-open') && placeDrop());

  // In the compact header the search icon opens the same take-over instead of Horizon's modal.
  document.addEventListener('click', (e) => {
    const icon = e.target.closest('#header-component .search-action, #header-component .search-action *');
    if (!icon || !desktop()) return;
    const input = document.querySelector('#header-component [data-pp-search-input]');
    if (!input) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    body.classList.add('pp-search-open');
    requestAnimationFrame(() => { placeDrop(); input.focus(); });
  }, true);

  /* 3. Member drawer: account icon (when logged out) and "Join" buttons open it */
  const memberDrawer = () => document.getElementById('pp-member-drawer');
  const openMember = () => {
    const d = memberDrawer();
    if (!d || d.open) return false;
    document.querySelectorAll('dialog.pp-drawer[open]').forEach((o) => o.close());
    d.showModal();
    document.documentElement.classList.add('pp-locked');
    return true;
  };
  document.addEventListener('click', (e) => {
    const join = e.target.closest('[data-pp-member]');
    if (join && memberDrawer()) { e.preventDefault(); openMember(); return; }
    if (window.ppCustomer) return;
    const acct = e.target.closest('#header-component .account-button, #header-component shopify-account, #header-component a[href*="/account"]');
    if (acct && memberDrawer()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      openMember();
    }
  }, true);
  document.addEventListener('click', (e) => {
    const d = memberDrawer();
    if (!d || !d.open) return;
    if (e.target === d || e.target.closest('#pp-member-drawer [data-pp-close]')) {
      d.close();
    }
  });
  memberDrawer()?.addEventListener('close', () => document.documentElement.classList.remove('pp-locked'));

  /* 4. Product page: size dropdown + "View all images" */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-pp-variant-select]');
    if (!sel) return;
    const opt = sel.selectedOptions[0];
    const scope = sel.closest('form') || document;
    const link = [...scope.querySelectorAll('[data-pp-variant-link]')].find((a) => a.getAttribute('href') === opt.dataset.url);
    if (link) link.click(); else window.location.href = opt.dataset.url;
  });
  document.addEventListener('click', (e) => {
    const more = e.target.closest('[data-pp-gallery-more]');
    if (!more) return;
    const gallery = more.closest('[data-pp-gallery]');
    const expanded = gallery.classList.toggle('is-expanded');
    more.setAttribute('aria-expanded', String(expanded));
    more.querySelector('[data-label]').textContent = expanded ? 'Show fewer images' : `View all images (${more.dataset.count})`;
    if (!expanded) gallery.scrollIntoView({ block: 'start', behavior: 'smooth' });
  });

  /* 5. Free gift guard: if the bag drops under the threshold, take the gift out again */
  let removing = false;
  const checkGift = () => {
    if (removing) return;
    const flag = document.querySelector('[data-pp-gift-remove]');
    const btn = flag?.querySelector('[data-pp-line-qty="0"]');
    if (!btn) return;
    removing = true;
    btn.click();
    setTimeout(() => { removing = false; }, 1500);
  };
  new MutationObserver(checkGift).observe(body, { childList: true, subtree: true });
  checkGift();
})();
